export interface GeocodingAddress {
  address1: string;
  address2?: string;
  city: string;
  state: string;
  countryCode: string;
  zipPostalCode: string;
}

export interface GeocodingResult {
  isVerified: boolean;
  isRooftop: boolean;
  partialMatch: boolean;
  suggestedAddress: GeocodingAddress | null;
  originalAddress: GeocodingAddress;
}

const addressLookupMap = {
  streetNumber: ['street_number', 'long_name'],
  street: ['route', 'long_name'],
  address2: ['subpremise', 'long_name'],
  city: ['locality', 'long_name'],
  state: ['administrative_area_level_1', 'short_name'],
  country: ['country', 'short_name'],
  zip: ['postal_code', 'short_name'],
};

const findAddressPart = (
  addressComponents: google.maps.GeocoderAddressComponent[],
  lookup: string
): string | null => {
  const addressLookup = addressLookupMap[lookup];
  if (!addressLookup) return null;

  const addressPart = addressComponents?.find(part =>
    part?.types?.includes(addressLookup[0])
  );

  return addressPart ? (addressPart[addressLookup[1]] as string) : null;
};

export const parseGeoAddress = async (
  address: GeocodingAddress
): Promise<GeocodingResult> => {
  const result: GeocodingResult = {
    isVerified: false,
    isRooftop: false,
    partialMatch: false,
    suggestedAddress: null,
    originalAddress: address,
  };

  if (typeof google === 'undefined' || !google.maps) {
    return result;
  }

  const geocoder = new google.maps.Geocoder();

  const addressString = [
    address.address1,
    address.city,
    address.state,
    address.zipPostalCode,
    address.countryCode,
  ]
    .filter(Boolean)
    .join(', ');

  try {
    const response = await geocoder.geocode({ address: addressString });

    if (response.results && response.results.length > 0) {
      const geoResult = response.results[0];

      result.isRooftop = geoResult.geometry?.location_type === 'ROOFTOP';
      result.partialMatch = geoResult.partial_match === true;

      const addressComponents = geoResult.address_components;
      if (addressComponents) {
        const streetNumber =
          findAddressPart(addressComponents, 'streetNumber') || '';
        const street = findAddressPart(addressComponents, 'street') || '';
        const city = findAddressPart(addressComponents, 'city') || '';
        const state = findAddressPart(addressComponents, 'state') || '';
        const countryCode = findAddressPart(addressComponents, 'country') || '';
        const zip = findAddressPart(addressComponents, 'zip') || '';

        result.suggestedAddress = {
          address1: (streetNumber + ' ' + street).trim(),
          city,
          state,
          countryCode,
          zipPostalCode: zip,
        };

        result.isVerified = result.isRooftop && !result.partialMatch;
      }
    }
  } catch (error) {
    console.error('Geocoding error:', error);
  }

  return result;
};
